/**
 * Cell Logs Panel
 * Displays step-by-step crawling progress for a selected grid cell
 *
 * Uses Ant Design 5's Timeline `items` prop API for proper rendering.
 */

import { useMemo } from 'react';
import { Card, Timeline, Typography, Tag, Space, Spin, Empty, Button } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  RocketOutlined,
  CompassOutlined,
  FileSearchOutlined,
  UnorderedListOutlined,
  SaveOutlined,
  WarningOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { useCellLogs } from '@/hooks/useCellLogs';
import type { CrawlStep } from '@/services/jobsApi';

const { Text, Title } = Typography;

interface CellLogsPanelProps {
  jobId: string;
  cellId: string;
  cellStatus?: string;
  onClose?: () => void;
}

/**
 * Status colors - distinct and accessible
 */
const STATUS_COLORS = {
  started: '#1677ff',    // Blue - in progress
  completed: '#52c41a',  // Green - success
  failed: '#ff4d4f',     // Red - error
  progress: '#faad14',   // Gold - active progress
} as const;

/**
 * Status tag colors for Ant Design Tag component
 */
const STATUS_TAG_COLORS = {
  started: 'processing',
  completed: 'success',
  failed: 'error',
  progress: 'warning',
} as const;

/**
 * Get icon component for step name
 */
function getStepIcon(step: string, color?: string): React.ReactNode {
  const style = color ? { color } : undefined;
  switch (step) {
    case 'initialization':
      return <RocketOutlined style={style} />;
    case 'navigation':
      return <CompassOutlined style={style} />;
    case 'consent_check':
      return <FileSearchOutlined style={style} />;
    case 'waiting_for_results':
      return <ClockCircleOutlined style={style} />;
    case 'scrolling':
      return <UnorderedListOutlined style={style} />;
    case 'extraction':
      return <DatabaseOutlined style={style} />;
    case 'saving':
      return <SaveOutlined style={style} />;
    case 'cell_scrape':
      return <WarningOutlined style={style} />;
    default:
      return <ClockCircleOutlined style={style} />;
  }
}

/**
 * Get human-readable step name
 */
function getStepLabel(step: string): string {
  const labels: Record<string, string> = {
    initialization: 'Initializing',
    navigation: 'Navigating to URL',
    consent_check: 'Checking consent dialog',
    waiting_for_results: 'Waiting for results',
    scrolling: 'Loading results',
    extraction: 'Extracting place details',
    saving: 'Saving to database',
    cell_scrape: 'Cell scrape',
  };
  return labels[step] || step.replace(/_/g, ' ');
}

/**
 * Format duration in milliseconds to human-readable string
 */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  if (minutes < 60) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Format timestamp to time string
 */
function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/**
 * Get timeline dot icon based on status
 */
function getStatusDot(status: CrawlStep['status']): React.ReactNode {
  const color = STATUS_COLORS[status] || STATUS_COLORS.started;
  switch (status) {
    case 'completed':
      return <CheckCircleOutlined style={{ fontSize: 16, color }} />;
    case 'failed':
      return <CloseCircleOutlined style={{ fontSize: 16, color }} />;
    case 'progress':
      return <LoadingOutlined style={{ fontSize: 16, color }} spin />;
    case 'started':
    default:
      return <LoadingOutlined style={{ fontSize: 16, color }} />;
  }
}

/**
 * Render the content for a single step
 */
function StepContent({ step }: { step: CrawlStep }) {
  const details = step.details ? Object.entries(step.details) : [];
  const statusColor = STATUS_COLORS[step.status] || STATUS_COLORS.started;
  const tagColor = STATUS_TAG_COLORS[step.status] || 'default';

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {/* Step header: icon, label, status, duration */}
      <Space wrap size={8}>
        {getStepIcon(step.step, statusColor)}
        <Text strong style={{ color: statusColor }}>
          {getStepLabel(step.step)}
        </Text>
        <Tag color={tagColor} style={{ margin: 0 }}>
          {step.status}
        </Tag>
        {step.duration !== undefined && step.duration > 0 && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            ({formatDuration(step.duration)})
          </Text>
        )}
      </Space>

      {/* Timestamp */}
      <Text type="secondary" style={{ fontSize: 11 }}>
        {formatTime(step.timestamp)}
      </Text>

      {/* Details section (if any) */}
      {details.length > 0 && (
        <div
          style={{
            marginTop: 4,
            padding: '6px 10px',
            background: 'var(--color-bg-spotlight, rgba(0,0,0,0.04))',
            borderRadius: 6,
            fontSize: 12,
            borderLeft: `3px solid ${statusColor}`,
          }}
        >
          {details.map(([key, value]) => (
            <div key={key} style={{ marginBottom: 2 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {key}:
              </Text>{' '}
              <Text style={{ fontSize: 12 }}>
                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
              </Text>
            </div>
          ))}
        </div>
      )}
    </Space>
  );
}

export function CellLogsPanel({
  jobId,
  cellId,
  cellStatus,
  onClose,
}: CellLogsPanelProps) {
  const isProcessing =
    cellStatus === 'running' ||
    cellStatus === 'processing' ||
    cellStatus === 'in_progress' ||
    cellStatus === 'queued';
  const { data, isLoading, error } = useCellLogs(jobId, cellId, isProcessing);

  const steps = data?.steps || [];

  // Build Timeline items using Ant Design 5's items prop API
  const timelineItems = useMemo(() => {
    return steps.map((step, index) => ({
      key: `${step.step}-${step.status}-${index}`,
      dot: getStatusDot(step.status),
      color: STATUS_COLORS[step.status] || STATUS_COLORS.started,
      children: <StepContent step={step} />,
    }));
  }, [steps]);

  return (
    <Card
      size="small"
      title={
        <Space>
          <Title level={5} style={{ margin: 0 }}>
            Cell Logs
          </Title>
          <Text type="secondary" code>
            {cellId}
          </Text>
          {isProcessing && (
            <LoadingOutlined style={{ color: STATUS_COLORS.started }} />
          )}
        </Space>
      }
      extra={
        onClose && (
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
          />
        )
      }
      styles={{
        body: {
          maxHeight: 450,
          overflowY: 'auto',
        },
      }}
    >
      {isLoading && (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Spin tip="Loading logs..." />
        </div>
      )}

      {error && (
        <Empty
          description={
            <Text type="danger">
              Failed to load logs:{' '}
              {error instanceof Error ? error.message : 'Unknown error'}
            </Text>
          }
        />
      )}

      {!isLoading && !error && steps.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text type="secondary">
              {cellStatus === 'pending'
                ? 'Waiting to start...'
                : 'No logs available yet'}
            </Text>
          }
        />
      )}

      {!isLoading && !error && steps.length > 0 && (
        <Timeline
          style={{ marginTop: 12, paddingTop: 4 }}
          items={timelineItems}
        />
      )}
    </Card>
  );
}

export default CellLogsPanel;
